import cn from 'classnames';
import { ReactComponent as CoinIcon } from '@assets/svg/coin.svg';
import { BREAKPOINTS } from '@consts/ui';
import { formatThousandNumber } from '@utils/utils';
import WinnerPlaceholderImg from '@assets/png/placeholder-img.png';
import Avatar from '@components/avatar';
import InfoCard from '@components/info-card';
import Labeler from '@components/labeler';
import CountdownTimer from '@components/countdown-timer';
import useReduxState from '@hooks/useReduxState';
import styles from './game.module.scss';
import { useContext, useEffect, useState } from 'react';
import { playersColorContext } from '@views/jackpot';
import useJackpotRoom from '@hooks/useJackpotRoom';

const Results = (): JSX.Element => {
    const [{ jackpot, chat: chatState, ui }] = useReduxState((state) => state);
    const { rooms } = jackpot;
    const { currentRoom } = useJackpotRoom();
    const { players: playersColors } = useContext(playersColorContext);

    const { current_game } = rooms[currentRoom];
    const winnerIndex = current_game.players.findIndex(
        (player) => player.user.id === current_game.winner_id,
    );
    const winner = current_game.players[winnerIndex];
    const playerColor =
        winnerIndex !== -1 ? playersColors[winnerIndex].color : 'none';
    // eslint-disable-next-line no-unused-vars
    const [_forceUpdate, setForceUpdate] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setForceUpdate(new Date().getTime());
        }, 1000);

        return () => {
            clearInterval(interval);
        };
    });

    /* Components */

    const avatar: JSX.Element = (
        <Labeler label="Winner" className={styles.avatar}>
            <Avatar
                src={
                    winner &&
                    Math.floor(Date.now() / 1000) -
                        current_game.game_metadata.ended_at >
                        11
                        ? winner.user.avatar_url
                        : WinnerPlaceholderImg
                }
                alt={
                    winner &&
                    Math.floor(Date.now() / 1000) -
                        current_game.game_metadata.ended_at >
                        11
                        ? winner.user.username
                        : ''
                }
                borderColor={
                    winnerIndex !== -1 &&
                    Math.floor(Date.now() / 1000) -
                        current_game.game_metadata.ended_at >
                        11
                        ? playerColor.slice(1, playerColor.length)
                        : 'none'
                }
            />
        </Labeler>
    );

    /* ClassNames */

    const containerClasses = cn(styles.ingame, 'panel-container', {
        [styles.chatIsVisible]: !chatState.collapsed,
    });

    return (
        <section className={containerClasses}>
            <div className={cn(styles.totalJackpot, 'upper-section')}>
                <Labeler
                    label="Total jackpot"
                    className={styles.totalJackpot__amountlabel}
                >
                    <InfoCard
                        color="green-gradient"
                        text={formatThousandNumber(current_game.bank / 100)}
                        icon={<CoinIcon />}
                        className={styles.totalJackpot__amount}
                    />
                </Labeler>
                {ui.windowWidth < BREAKPOINTS.LG &&
                    chatState.collapsed &&
                    avatar}
                {!chatState.collapsed &&
                    ui.windowWidth < BREAKPOINTS._2XXL &&
                    avatar}
            </div>

            <div className={cn(styles.info, 'lower-section')}>
                <div className={styles.info__container}>
                    {ui.windowWidth >= BREAKPOINTS.LG &&
                        chatState.collapsed &&
                        avatar}
                    {ui.windowWidth >= BREAKPOINTS._2XXL &&
                        !chatState.collapsed &&
                        avatar}
                    {Math.floor(Date.now() / 1000) -
                        current_game.game_metadata.ended_at >
                        8 && (
                        <Labeler
                            label="Ticket"
                            className={styles.ingame__ticket}
                        >
                            <InfoCard
                                color="dark-gradient"
                                text={
                                    current_game.roll_stake
                                        ? `#${formatThousandNumber(
                                              current_game.roll_stake,
                                          )}`
                                        : '??'
                                }
                                className={styles.info__ticket}
                            />
                        </Labeler>
                    )}
                </div>
                <Labeler label="Next Game">
                    <CountdownTimer
                        seconds={rooms[currentRoom].countdown_to_new_game}
                        start={current_game.game_metadata.status === 'finished'}
                        className={styles.info__timer}
                        startingDate={current_game.game_metadata.ended_at}
                    />
                </Labeler>
            </div>
        </section>
    );
};

export default Results;
